import { useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Search, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Textarea } from '@/components/ui/Textarea';
import { Badge } from '@/components/ui/Badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { ItemPicker } from '@/components/inventory/ItemPicker';
import { useItems, type Item } from '@/features/inventory/hooks';
import { useSalesOrders, type SalesOrderRow } from '@/features/sales/hooks';
import {
  CHANNEL_RETURN_PROFILES,
  DISPOSITIONS,
  DISPOSITION_LABELS,
  LABEL_PAYERS,
  LABEL_PAYER_LABELS,
  RECEIPT_MODE_LABELS,
  RESOLUTION_LABELS,
  RETURN_CHANNEL_ORDER,
  RETURN_REASONS,
  RETURN_REASON_LABELS,
  useReturnCustomers,
  useSaveReturn,
  type Disposition,
  type LabelPayer,
  type ReceiptMode,
  type Resolution,
  type ReturnReason,
  type ReturnRow,
  type ReturnStatus,
} from '@/features/returns/hooks';
import { centsToInputDollars, formatCurrency, formatDate, inputDollarsToCents } from '@/lib/formatters';
import { cn } from '@/lib/utils';
import type { SalesChannelKind } from '@/types/database';

const NONE = '__none';

interface LineDraft {
  key: string;
  id: string | null;
  itemId: string | null;
  itemSku: string | null;
  itemName: string | null;
  sku: string;
  description: string;
  orderLineRef: string | null;
  ordered: number | null;
  received: number;
  quantity: string;
  unitPrice: string;
  disposition: Disposition | '';
}

let lineSeq = 0;
const nextKey = () => `line-${++lineSeq}`;

function lineFromItem(item: Item): LineDraft {
  return {
    key: nextKey(),
    id: null,
    itemId: item.id,
    itemSku: item.sku,
    itemName: item.name,
    sku: item.sku,
    description: '',
    orderLineRef: null,
    ordered: null,
    received: 0,
    quantity: '1',
    unitPrice: '',
    disposition: '',
  };
}

interface ReturnDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Edit an existing RMA. */
  existing?: ReturnRow | null;
  /** Start a new RMA from an order (e.g. the Orders drawer). */
  initialOrder?: SalesOrderRow | null;
  onSaved?: (id: string) => void;
}

/**
 * Create or edit an RMA. Picking the channel seeds receipt mode,
 * resolution, and label payer from that channel's profile; linking a
 * synced order pre-fills the customer and its line items (matched to the
 * catalog by SKU). Amazon orders sync without line items today, so those
 * lines are added from the catalog.
 */
export function ReturnDialog({ open, onOpenChange, existing, initialOrder, onSaved }: ReturnDialogProps) {
  const save = useSaveReturn();
  const { data: orders } = useSalesOrders();
  const { data: items } = useItems();
  const { data: customers } = useReturnCustomers();

  const [channelKind, setChannelKind] = useState<SalesChannelKind>('shopify');
  const [order, setOrder] = useState<SalesOrderRow | null>(null);
  const [orderQuery, setOrderQuery] = useState('');
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [externalOrderRef, setExternalOrderRef] = useState('');
  const [externalReturnId, setExternalReturnId] = useState('');
  const [reasonCode, setReasonCode] = useState<ReturnReason>('defective');
  const [reasonDetail, setReasonDetail] = useState('');
  const [receiptMode, setReceiptMode] = useState<ReceiptMode>('ship_to_us');
  const [resolution, setResolution] = useState<Resolution>('refund');
  const [labelPaidBy, setLabelPaidBy] = useState<LabelPayer | null>(null);
  const [carrier, setCarrier] = useState('');
  const [tracking, setTracking] = useState('');
  const [restockingFee, setRestockingFee] = useState('');
  const [notes, setNotes] = useState('');
  const [initialStatus, setInitialStatus] = useState<ReturnStatus>('authorized');
  const [lines, setLines] = useState<LineDraft[]>([]);
  /** 'new' = adding a line; otherwise the key of the line being mapped. */
  const [pickerFor, setPickerFor] = useState<string | null>(null);

  const isEdit = !!existing;
  const profile = CHANNEL_RETURN_PROFILES[channelKind];

  const itemsBySku = useMemo(() => {
    const map = new Map<string, Item>();
    for (const it of items ?? []) map.set(it.sku.trim().toLowerCase(), it);
    return map;
  }, [items]);

  const applyChannelDefaults = (kind: SalesChannelKind) => {
    const p = CHANNEL_RETURN_PROFILES[kind];
    setReceiptMode(p.defaultReceiptMode);
    setResolution(p.defaultResolution);
    setLabelPaidBy(p.defaultLabelPaidBy);
  };

  const applyOrder = (o: SalesOrderRow) => {
    setOrder(o);
    setOrderQuery('');
    setCustomerName(o.customer_name ?? '');
    setCustomerEmail(o.customer_email ?? '');
    setExternalOrderRef(o.external_id ?? '');
    const single = o.line_items.length === 1;
    setLines(
      o.line_items.map((li) => {
        const match = li.sku ? itemsBySku.get(li.sku.trim().toLowerCase()) : undefined;
        return {
          key: nextKey(),
          id: null,
          itemId: match?.id ?? null,
          itemSku: match?.sku ?? null,
          itemName: match?.name ?? null,
          sku: li.sku ?? '',
          description: li.name,
          orderLineRef: li.external_line_id ?? null,
          ordered: li.quantity,
          received: 0,
          quantity: single ? String(li.quantity) : '',
          unitPrice: centsToInputDollars(li.unit_price_cents),
          disposition: '',
        };
      }),
    );
  };

  // Map SKU-only lines to the catalog once items load.
  useEffect(() => {
    if (itemsBySku.size === 0) return;
    setLines((prev) =>
      prev.map((l) => {
        if (l.itemId || !l.sku.trim()) return l;
        const match = itemsBySku.get(l.sku.trim().toLowerCase());
        return match ? { ...l, itemId: match.id, itemSku: match.sku, itemName: match.name } : l;
      }),
    );
  }, [itemsBySku]);

  // Seed once per open, not on every refetch of `existing`.
  const seededFor = useRef<string | null>(null);
  useEffect(() => {
    if (!open) {
      seededFor.current = null;
      return;
    }
    const seedKey = existing?.id ?? `new:${initialOrder?.id ?? ''}`;
    if (seededFor.current === seedKey) return;
    seededFor.current = seedKey;
    setPickerFor(null);
    setOrderQuery('');
    if (existing) {
      setChannelKind(existing.channel_kind);
      setOrder(null);
      setCustomerId(existing.customer_id);
      setCustomerName(existing.customer_name ?? '');
      setCustomerEmail(existing.customer_email ?? '');
      setExternalOrderRef(existing.external_order_ref ?? '');
      setExternalReturnId(existing.external_return_id ?? '');
      setReasonCode(existing.reason_code);
      setReasonDetail(existing.reason_detail ?? '');
      setReceiptMode(existing.receipt_mode);
      setResolution(existing.resolution);
      setLabelPaidBy(existing.label_paid_by);
      setCarrier(existing.carrier ?? '');
      setTracking(existing.tracking_number ?? '');
      setRestockingFee(existing.restocking_fee_cents ? centsToInputDollars(existing.restocking_fee_cents) : '');
      setNotes(existing.notes ?? '');
      setLines(
        existing.return_lines.map((l) => ({
          key: nextKey(),
          id: l.id,
          itemId: l.item_id,
          itemSku: l.items?.sku ?? null,
          itemName: l.items?.name ?? null,
          sku: l.sku ?? '',
          description: l.description ?? '',
          orderLineRef: l.order_line_ref,
          ordered: null,
          received: l.received_quantity,
          quantity: String(l.quantity),
          unitPrice: centsToInputDollars(l.unit_price_cents),
          disposition: l.disposition ?? '',
        })),
      );
      return;
    }
    const kind =
      initialOrder && initialOrder.channel.kind !== 'unknown' ? initialOrder.channel.kind : 'shopify';
    setChannelKind(kind);
    applyChannelDefaults(kind);
    setCustomerId(null);
    setExternalReturnId('');
    setReasonCode('defective');
    setReasonDetail('');
    setCarrier('');
    setTracking('');
    setRestockingFee('');
    setNotes('');
    setInitialStatus(kind === 'amazon' ? 'authorized' : 'requested');
    if (initialOrder) {
      applyOrder(initialOrder);
    } else {
      setOrder(null);
      setCustomerName('');
      setCustomerEmail('');
      setExternalOrderRef('');
      setLines([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seed once per open
  }, [open, existing, initialOrder]);

  // Existing RMAs only store the order id; resolve it once orders load.
  useEffect(() => {
    if (!open || !existing?.sales_order_id || order) return;
    const found = (orders ?? []).find((o) => o.id === existing.sales_order_id);
    if (found) setOrder(found);
  }, [open, existing, orders, order]);

  const orderMatches = useMemo(() => {
    const q = orderQuery.trim().toLowerCase();
    if (!q) return [];
    return (orders ?? [])
      .filter((o) => o.channel.kind === channelKind)
      .filter(
        (o) =>
          o.so_number.toLowerCase().includes(q) ||
          (o.external_id ?? '').toLowerCase().includes(q) ||
          (o.customer_email ?? '').toLowerCase().includes(q) ||
          (o.customer_name ?? '').toLowerCase().includes(q),
      )
      .slice(0, 6);
  }, [orders, orderQuery, channelKind]);

  const updateLine = (key: string, patch: Partial<LineDraft>) =>
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  const handlePick = (item: Item | null) => {
    if (!item || !pickerFor) return;
    if (pickerFor === 'new') {
      setLines((prev) => [...prev, lineFromItem(item)]);
    } else {
      updateLine(pickerFor, { itemId: item.id, itemSku: item.sku, itemName: item.name });
    }
    setPickerFor(null);
  };

  const addFreeTextLine = () =>
    setLines((prev) => [
      ...prev,
      {
        key: nextKey(),
        id: null,
        itemId: null,
        itemSku: null,
        itemName: null,
        sku: '',
        description: '',
        orderLineRef: null,
        ordered: null,
        received: 0,
        quantity: '1',
        unitPrice: '',
        disposition: '',
      },
    ]);

  const returningLines = lines.filter((l) => Number(l.quantity) > 0);
  const expectedValue = returningLines.reduce((acc, l) => {
    try {
      return acc + (inputDollarsToCents(l.unitPrice) ?? 0) * Number(l.quantity);
    } catch {
      return acc;
    }
  }, 0);

  const handleChannelChange = (kind: SalesChannelKind) => {
    if (kind === channelKind) return;
    setChannelKind(kind);
    applyChannelDefaults(kind);
    if (order && order.channel.kind !== kind) setOrder(null);
    if (kind !== 'b2b_portal') setCustomerId(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    let payloadLines;
    let feeCents: number | null;
    try {
      feeCents = inputDollarsToCents(restockingFee);
      payloadLines = lines
        .filter((l) => l.quantity.trim() !== '' && Number(l.quantity) > 0)
        .map((l) => {
          const qty = Number(l.quantity);
          const label = l.itemSku ?? l.sku ?? l.description;
          if (!Number.isInteger(qty)) throw new Error(`${label || 'A line'}: quantity must be a whole number.`);
          if (l.ordered != null && qty > l.ordered) {
            throw new Error(`${label}: only ${l.ordered} were on the order.`);
          }
          if (!l.itemId && !l.sku.trim() && !l.description.trim()) {
            throw new Error('Every line needs a catalog item, SKU, or description.');
          }
          return {
            id: l.id,
            item_id: l.itemId,
            sku: l.itemSku ?? (l.sku.trim() || null),
            description: l.description.trim() || null,
            order_line_ref: l.orderLineRef,
            quantity: qty,
            unit_price_cents: inputDollarsToCents(l.unitPrice),
            disposition: l.disposition || null,
          };
        });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Check the RMA lines.');
      return;
    }
    if (payloadLines.length === 0) {
      toast.error('Enter a return quantity on at least one line.');
      return;
    }

    try {
      const id = await save.mutateAsync({
        id: existing?.id ?? null,
        status: isEdit ? undefined : receiptMode === 'ship_to_us' ? initialStatus : 'authorized',
        sales_order_id: order?.id ?? (isEdit ? existing?.sales_order_id ?? null : null),
        sales_channel_id: order?.channel.id ?? existing?.sales_channel_id ?? null,
        channel_kind: channelKind,
        customer_id: customerId,
        customer_name: customerName || null,
        customer_email: customerEmail || null,
        external_return_id: externalReturnId || null,
        external_order_ref: externalOrderRef || null,
        reason_code: reasonCode,
        reason_detail: reasonDetail || null,
        receipt_mode: receiptMode,
        resolution,
        label_paid_by: labelPaidBy,
        carrier: carrier || null,
        tracking_number: tracking || null,
        restocking_fee_cents: feeCents,
        notes: notes || null,
        lines: payloadLines,
      });
      toast.success(isEdit ? `${existing?.rma_number} updated` : 'RMA created');
      onSaved?.(id);
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not save the RMA.');
    }
  };

  const submitting = save.isPending;
  const customerRows = customers ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-5xl overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{isEdit ? `Edit ${existing?.rma_number}` : 'New return (RMA)'}</DialogTitle>
          </DialogHeader>

          <div className="mt-5 space-y-6">
            {/* Channel */}
            <section className="space-y-3">
              <div className="flex flex-wrap gap-2">
                {RETURN_CHANNEL_ORDER.map((kind) => (
                  <button
                    key={kind}
                    type="button"
                    onClick={() => handleChannelChange(kind)}
                    disabled={isEdit && !!existing?.sales_channel_id}
                    className={cn(
                      'rounded-full border px-3 py-1 text-sm transition-colors disabled:opacity-50',
                      channelKind === kind
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border hover:bg-secondary/60',
                    )}
                  >
                    {CHANNEL_RETURN_PROFILES[kind].label}
                  </button>
                ))}
              </div>
              <div className="rounded-md border border-border bg-secondary/30 px-4 py-3 text-sm text-muted-foreground">
                Resolution: {profile.resolutionVenue}
                {profile.windowDays ? ` · ${profile.windowDays}-day window from delivery` : ''}
              </div>
            </section>

            {/* Source order + customer */}
            <section className="grid gap-4 lg:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Original order</Label>
                {order ? (
                  <div className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2 text-sm">
                    <div className="min-w-0">
                      <div className="font-medium">{order.so_number}</div>
                      <div className="truncate text-xs text-muted-foreground">
                        {formatDate(order.ordered_at)} · {order.customer_name || order.customer_email || 'No customer'} ·{' '}
                        {formatCurrency(order.total_cents)}
                      </div>
                    </div>
                    {!isEdit && (
                      <Button type="button" variant="ghost" size="sm" onClick={() => setOrder(null)}>
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-1">
                    <div className="relative">
                      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        value={orderQuery}
                        onChange={(e) => setOrderQuery(e.target.value)}
                        placeholder={`Search ${profile.label} orders by #, email, or name…`}
                        className="pl-9"
                      />
                    </div>
                    {orderMatches.length > 0 && (
                      <div className="max-h-48 overflow-y-auto rounded-md border border-border">
                        {orderMatches.map((o) => (
                          <button
                            key={o.id}
                            type="button"
                            onClick={() => applyOrder(o)}
                            className="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-sm hover:bg-secondary/60"
                          >
                            <span className="font-medium">{o.so_number}</span>
                            <span className="truncate text-xs text-muted-foreground">
                              {formatDate(o.ordered_at)} · {o.customer_name || o.customer_email || '—'}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                {channelKind === 'b2b_portal' && (
                  <div className="space-y-1.5 sm:col-span-2">
                    <Label>Wholesale customer</Label>
                    <Select
                      value={customerId ?? NONE}
                      onValueChange={(v) => {
                        const c = customerRows.find((row) => row.id === v);
                        setCustomerId(c?.id ?? null);
                        if (c) {
                          setCustomerName(c.name);
                          setCustomerEmail(c.email ?? '');
                        }
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Pick a customer" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NONE}>Not in the customer list</SelectItem>
                        {customerRows.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label htmlFor="rma-cust-name">Customer name</Label>
                  <Input id="rma-cust-name" value={customerName} onChange={(e) => setCustomerName(e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="rma-cust-email">Customer email</Label>
                  <Input
                    id="rma-cust-email"
                    type="email"
                    value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="rma-ext-order">{profile.label} order #</Label>
                  <Input
                    id="rma-ext-order"
                    value={externalOrderRef}
                    onChange={(e) => setExternalOrderRef(e.target.value)}
                    placeholder={channelKind === 'amazon' ? '111-1234567-1234567' : 'Optional'}
                    className="font-mono text-xs"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="rma-ext-return">{profile.label} return / RMA id</Label>
                  <Input
                    id="rma-ext-return"
                    value={externalReturnId}
                    onChange={(e) => setExternalReturnId(e.target.value)}
                    placeholder="Optional"
                    className="font-mono text-xs"
                  />
                </div>
              </div>
            </section>

            {/* Reason + handling */}
            <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div className="space-y-1.5">
                <Label>Reason</Label>
                <Select value={reasonCode} onValueChange={(v) => setReasonCode(v as ReturnReason)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RETURN_REASONS.map((r) => (
                      <SelectItem key={r} value={r}>
                        {RETURN_REASON_LABELS[r]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Who receives it</Label>
                <Select value={receiptMode} onValueChange={(v) => setReceiptMode(v as ReceiptMode)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {profile.receiptModes.map((m) => (
                      <SelectItem key={m} value={m}>
                        {RECEIPT_MODE_LABELS[m]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Resolution</Label>
                <Select value={resolution} onValueChange={(v) => setResolution(v as Resolution)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {profile.resolutions.map((r) => (
                      <SelectItem key={r} value={r}>
                        {RESOLUTION_LABELS[r]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {!isEdit && receiptMode === 'ship_to_us' ? (
                <div className="space-y-1.5">
                  <Label>Start as</Label>
                  <Select value={initialStatus} onValueChange={(v) => setInitialStatus(v as ReturnStatus)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="requested">Requested (needs approval)</SelectItem>
                      <SelectItem value="authorized">Authorized</SelectItem>
                      <SelectItem value="in_transit">In transit</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="space-y-1.5">
                  <Label htmlFor="rma-fee">Restocking fee</Label>
                  <Input
                    id="rma-fee"
                    value={restockingFee}
                    onChange={(e) => setRestockingFee(e.target.value)}
                    inputMode="decimal"
                    placeholder="0.00"
                  />
                </div>
              )}
              <div className="space-y-1.5 sm:col-span-2 lg:col-span-4">
                <Label htmlFor="rma-reason-detail">Customer's explanation</Label>
                <Textarea
                  id="rma-reason-detail"
                  value={reasonDetail}
                  onChange={(e) => setReasonDetail(e.target.value)}
                  placeholder="What the customer reported, photos received, troubleshooting tried…"
                  rows={2}
                  maxLength={2000}
                />
              </div>
            </section>

            {receiptMode === 'ship_to_us' && (
              <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <div className="space-y-1.5">
                  <Label>Return label paid by</Label>
                  <Select
                    value={labelPaidBy ?? NONE}
                    onValueChange={(v) => setLabelPaidBy(v === NONE ? null : (v as LabelPayer))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>Not set</SelectItem>
                      {LABEL_PAYERS.map((p) => (
                        <SelectItem key={p} value={p}>
                          {LABEL_PAYER_LABELS[p]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="rma-carrier">Carrier</Label>
                  <Input id="rma-carrier" value={carrier} onChange={(e) => setCarrier(e.target.value)} placeholder="UPS, FedEx…" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="rma-tracking">Tracking #</Label>
                  <Input
                    id="rma-tracking"
                    value={tracking}
                    onChange={(e) => setTracking(e.target.value)}
                    className="font-mono text-xs"
                  />
                </div>
                {!isEdit && (
                  <div className="space-y-1.5">
                    <Label htmlFor="rma-fee-2">Restocking fee</Label>
                    <Input
                      id="rma-fee-2"
                      value={restockingFee}
                      onChange={(e) => setRestockingFee(e.target.value)}
                      inputMode="decimal"
                      placeholder="0.00"
                    />
                  </div>
                )}
              </section>
            )}

            {/* Lines */}
            <section className="space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-sm font-medium">Items being returned</h3>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={() => setPickerFor('new')}>
                    <Plus className="h-4 w-4" />
                    Catalog item
                  </Button>
                  <Button type="button" variant="ghost" size="sm" onClick={addFreeTextLine}>
                    <Plus className="h-4 w-4" />
                    Unlisted SKU
                  </Button>
                </div>
              </div>

              {lines.length === 0 ? (
                <p className="rounded-md border border-dashed border-border bg-secondary/40 px-3 py-4 text-center text-xs text-muted-foreground">
                  No items yet.
                </p>
              ) : (
                <div className="overflow-x-auto rounded-md border border-border">
                  <table className="w-full text-sm">
                    <thead className="bg-secondary/50 text-xs text-muted-foreground">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium">Item</th>
                        <th className="w-20 px-2 py-2 text-right font-medium">Ordered</th>
                        <th className="w-24 px-2 py-2 text-right font-medium">Returning</th>
                        <th className="w-28 px-2 py-2 text-right font-medium">Unit price</th>
                        <th className="w-44 px-2 py-2 text-left font-medium">Planned disposition</th>
                        <th className="w-10 px-2 py-2" />
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((l) => (
                        <tr key={l.key} className="border-t border-border align-top">
                          <td className="px-3 py-2">
                            {l.itemId ? (
                              <>
                                <div className="font-mono text-xs text-muted-foreground">{l.itemSku}</div>
                                <div className="truncate">{l.itemName}</div>
                              </>
                            ) : (
                              <div className="space-y-1">
                                <div className="flex gap-1">
                                  <Input
                                    value={l.sku}
                                    onChange={(e) => updateLine(l.key, { sku: e.target.value })}
                                    placeholder="SKU"
                                    className="h-8 w-28 font-mono text-xs"
                                  />
                                  <Input
                                    value={l.description}
                                    onChange={(e) => updateLine(l.key, { description: e.target.value })}
                                    placeholder="Description"
                                    className="h-8"
                                  />
                                </div>
                                <button
                                  type="button"
                                  onClick={() => setPickerFor(l.key)}
                                  className="text-[11px] text-fresh-salmon-dark underline-offset-2 hover:underline"
                                >
                                  Not in catalog — map to an item
                                </button>
                              </div>
                            )}
                            {l.received > 0 && (
                              <Badge variant="muted" className="mt-1">
                                {l.received} received
                              </Badge>
                            )}
                          </td>
                          <td className="px-2 py-2 text-right tabular-nums text-muted-foreground">
                            {l.ordered ?? '—'}
                          </td>
                          <td className="px-2 py-2">
                            <Input
                              value={l.quantity}
                              onChange={(e) => updateLine(l.key, { quantity: e.target.value.replace(/[^\d]/g, '') })}
                              inputMode="numeric"
                              placeholder="0"
                              aria-label="Quantity returning"
                              className="h-8 text-right tabular-nums"
                            />
                          </td>
                          <td className="px-2 py-2">
                            <Input
                              value={l.unitPrice}
                              onChange={(e) => updateLine(l.key, { unitPrice: e.target.value })}
                              inputMode="decimal"
                              placeholder="0.00"
                              aria-label="Unit price paid"
                              className="h-8 text-right tabular-nums"
                            />
                          </td>
                          <td className="px-2 py-2">
                            <Select
                              value={l.disposition || NONE}
                              onValueChange={(v) =>
                                updateLine(l.key, { disposition: v === NONE ? '' : (v as Disposition) })
                              }
                            >
                              <SelectTrigger className="h-8">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value={NONE}>Decide on receipt</SelectItem>
                                {DISPOSITIONS.map((d) => (
                                  <SelectItem key={d} value={d}>
                                    {DISPOSITION_LABELS[d]}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-2 py-2">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              disabled={l.received > 0}
                              onClick={() => setLines((prev) => prev.filter((x) => x.key !== l.key))}
                              aria-label="Remove line"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {pickerFor && (
                <div className="space-y-2 rounded-md border border-border p-3">
                  <ItemPicker
                    label={pickerFor === 'new' ? 'Add a catalog item' : 'Map this line to a catalog item'}
                    value={null}
                    onChange={handlePick}
                    autoFocus
                  />
                  <div className="flex justify-end">
                    <Button type="button" variant="ghost" size="sm" onClick={() => setPickerFor(null)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </section>

            <div className="space-y-1.5">
              <Label htmlFor="rma-notes">Internal notes</Label>
              <Textarea
                id="rma-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                maxLength={2000}
              />
            </div>
          </div>

          <DialogFooter className="mt-6 sm:items-center">
            {returningLines.length > 0 && (
              <span className="mr-auto text-xs text-muted-foreground">
                {returningLines.reduce((acc, l) => acc + Number(l.quantity), 0)} units · {formatCurrency(expectedValue)} at
                price paid
              </span>
            )}
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting || returningLines.length === 0}>
              {submitting ? 'Saving…' : isEdit ? 'Save changes' : 'Create RMA'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
